import { User } from 'features/auth';
import { useGetQuestionnaireInfo } from 'features/patient/_utils/hook-useGetQuestionnaireInfo';
import { PatientInfoState } from 'features/patient/_utils/patient-info/patient-info-slice-types';
import { QuestionnaireControlFlowState } from 'features/patient/assessment/assessment-controller/questionnaire-control-flow-slice-types';
import { PrivateRoute } from 'navigation/router/components/routes/private-route';
import { useMemo } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { PatientRoutes } from 'routes';
import {
  getPatientLinkUrl,
  PatientRoutePattern
} from '../utils/patient-route-helpers';
import { filterPatientRoutes } from '../utils/patient-routes-filter';
import { isLastPatientAssessmentExpired } from '../utils/user-needs-assessment';

export function PatientRouter(props: {
  patientId: string;
  patientInfo: PatientInfoState;
  user: User;
}) {
  const { state: questionnaireInfo } = useGetQuestionnaireInfo(props.patientId);

  /**
   * Resolve the routes that are available for the user
   */
  const { routes: availableRoutes, redirectPath } = useResolveAvailableRoutes({
    ...props,
    questionnaireInfo
  });

  /**
   * Case  1: Show nothing if the questionnaire info is loading
   */
  if (questionnaireInfo.isLoading) {
    return <p>Loading questionnaire information</p>;
  }

  /**
   * Case 2: Redirect the user to the correct page if the URL patient ID doesn't match their ID
   */
  if (props.patientId !== props.user.uid) {
    return (
      <Redirect to={getPatientLinkUrl(PatientRoutes.Home, props.user.uid)} />
    );
  }

  return (
    <Switch>
      {availableRoutes.map(route => (
        <PrivateRoute
          {...route}
          exact
          path={PatientRoutePattern + route.path}
          key={`path-${JSON.stringify(route.path)}`}
          redirectPath={'../'}
        />
      ))}

      <Route
        // Handler when the browser redirects to the second base patient route and the route did not match any earlier routes
        path={[PatientRoutePattern]}
        render={() => <Redirect to={redirectPath} />}
      />
    </Switch>
  );
}

function useResolveAvailableRoutes(args: {
  patientId: string;
  patientInfo: PatientInfoState;
  user: User;
  questionnaireInfo: QuestionnaireControlFlowState;
}) {
  const patientIsDoingAnAssessment =
    args.questionnaireInfo.assessmentData !== null;

  const userNeedsAnAssessment =
    !patientIsDoingAnAssessment &&
    (args.patientInfo.patient.canStartAssessments ||
      isLastPatientAssessmentExpired(args.patientInfo));

  const redirectPath = useMemo(
    () =>
      resolveRedirectPath({
        patientId: args.patientId,
        user: args.user,
        patientInfo: args.patientInfo,
        patientIsDoingAnAssessment: patientIsDoingAnAssessment,
        questionnaireInfo: args.questionnaireInfo,
        patientNeedsAssessment: userNeedsAnAssessment
      }),
    [
      args.patientId,
      args.user,
      args.patientInfo,
      args.questionnaireInfo,
      patientIsDoingAnAssessment,
      userNeedsAnAssessment
    ]
  );

  const routes = useMemo(() => {
    if (args.patientInfo.isLoading) return [];
    /**
     * A Test-Taker needs an assessment if:
     * 1. They can start assessments.
     * 2. They are not currently doing an assessment.
     * 3. `isLastPatientAssessmentExpired` returns `true`
     */

    return filterPatientRoutes({
      role: args.user.role,
      options: {
        useBitmask: false,
        userIsBeingOnboarded: args.patientInfo.isBeingOnboarded,
        userIsDoingAnAssessment: patientIsDoingAnAssessment,
        userNeedsAnAssessment: userNeedsAnAssessment
      }
    });
  }, [
    args.user.role,
    args.patientInfo,
    patientIsDoingAnAssessment,
    userNeedsAnAssessment
  ]);

  return { redirectPath, routes };
}

function resolveRedirectPath(args: {
  user: User;
  patientInfo: PatientInfoState;
  patientId: string;
  patientIsDoingAnAssessment: boolean;
  patientNeedsAssessment: boolean;
  questionnaireInfo: QuestionnaireControlFlowState;
}) {
  /**
   * Redirect the user to the onboarding profile if they have not completed it yet
   */
  if (args.patientInfo.isBeingOnboarded) {
    return getPatientLinkUrl(
      PatientRoutes.PatientOnboardingProfile,
      args.patientId
    );
  }

  /**
   * 2025-12-16
   * If the user does not need an assessment, they will be redirected to the AssessmentComplete page
   */
  if (
    !args.patientInfo.patient.canStartAssessments &&
    !args.patientNeedsAssessment
  ) {
    return getPatientLinkUrl(PatientRoutes.AssessmentComplete, args.patientId);
  }

  /**
   * The user cannot start new assessments. Push them to the patient home page
   */
  if (!args.patientInfo.patient.canStartAssessments) {
    return getPatientLinkUrl(PatientRoutes.Home, args.patientId);
  }

  /**
   * The user is completing an assessment. Push them to the assessment home page
   */
  if (args.patientIsDoingAnAssessment) {
    return getPatientLinkUrl(PatientRoutes.AssessmentResume, args.patientId);
  }

  /**
   * At this point we know:
   * 1. The user has finished onboarding.
   * 2. The user can start assessments.
   * 3. The user is not currently in an assessment.
   */

  // /**
  //  * The user needs to do an assessment
  //  */
  // if (assessmentExpired) {
  //   return getPatientLinkUrl(PatientRoutes.AssessmentStart, args.patientId);
  // }

  /**
   * Default case: The user does not need to complete any actions
   */
  return getPatientLinkUrl(PatientRoutes.Home, args.patientId);
}
